import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from '@/components/ui/sheet';
import { CheckCircle, AlertCircle, Info, Layers } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { FieldCompletenessEntry } from '@/services/dashboardService';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';
import { useEffectiveAttributes } from '@/hooks/useEffectiveAttributes';
import { ENABLE_EFFECTIVE_ATTRIBUTES_V2 } from '@/config/featureFlags';
import { translateFieldName } from '@/utils/fieldTranslations';

// Keep this for backward compatibility during transition
interface FieldStatus {
  key: string;
  label: string;
  weight: number;
  complete: boolean;
}

interface CompletenessDrilldownProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  percentage: number;
  fieldStatus: FieldCompletenessEntry[] | FieldStatus[];
  productId: number;
  locale?: string;
  channel?: string;
}

export const CompletenessDrilldown: React.FC<CompletenessDrilldownProps> = ({
  open,
  onOpenChange,
  percentage: _percentage,
  fieldStatus,
  productId,
  locale,
  channel,
}) => {
  const i18n = useProductDetailPageTranslations();
  const labels = i18n.getCompletenessDrilldownLabels;
  const { data: groups, isLoading } = useEffectiveAttributes(
    productId,
    locale,
    channel,
    ENABLE_EFFECTIVE_ATTRIBUTES_V2
  );

  if (!ENABLE_EFFECTIVE_ATTRIBUTES_V2) {
    // Fallback to legacy completeness calculation
    return (
      <div className="rounded-lg border p-4">
        <div className="mb-2 flex items-center gap-2">
          <Layers className="size-4 text-gray-500" />
          <span className="text-sm font-medium">Attributes Completeness</span>
        </div>
        <div className="text-sm text-gray-500">
          Enable Effective Attributes V2 for detailed completeness tracking
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="animate-pulse rounded-lg border p-4">
        <div className="mb-2 h-4 rounded bg-gray-200" />
        <div className="h-2 rounded bg-gray-200" />
      </div>
    );
  }

  if (!groups || groups.length === 0) {
    return (
      <div className="rounded-lg border p-4">
        <div className="mb-2 flex items-center gap-2">
          <Layers className="size-4 text-gray-500" />
          <span className="text-sm font-medium">Attributes Completeness</span>
        </div>
        <div className="text-sm text-gray-500">No attribute groups configured for this product</div>
      </div>
    );
  }

  // Calculate completeness
  const total = groups.reduce((sum, g) => sum + g.attributes.length, 0);
  const filled = groups.reduce(
    (sum, g) => sum + g.attributes.filter(a => a.value != null && a.value !== '').length,
    0
  );

  const percent = total > 0 ? Math.round((filled / total) * 100) : 0;

  // Calculate required vs optional completeness
  const requiredGroups = groups.filter(g => g.required);
  const requiredTotal = requiredGroups.reduce((sum, g) => sum + g.attributes.length, 0);
  const requiredFilled = requiredGroups.reduce(
    (sum, g) => sum + g.attributes.filter(a => a.value != null && a.value !== '').length,
    0
  );
  // const _requiredPercent =
  //   requiredTotal > 0 ? Math.round((requiredFilled / requiredTotal) * 100) : 100;

  // Helper function to safely access field properties
  const getFieldKey = (field: FieldCompletenessEntry | FieldStatus): string => {
    if ('key' in field) {
      return field.key;
    }
    return field.field; // FieldCompletenessEntry uses 'field' property
  };

  const getFieldLabel = (field: FieldCompletenessEntry | FieldStatus): string => {
    const originalFieldName = 'label' in field ? field.label : field.field;
    // Translate field names that come from backend with hardcoded English prefixes
    return translateFieldName(originalFieldName, i18n.getTabsText);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[400px] overflow-y-auto sm:w-[540px] md:w-[600px]">
        <SheetHeader className="mb-5">
          <SheetTitle>
            {i18n.getCompletenessDrilldownText('title') || 'Data Completeness Details'}
          </SheetTitle>
          <SheetDescription>
            {i18n.getCompletenessDrilldownText('description') ||
              'Review the completeness of your product data fields'}
          </SheetDescription>
        </SheetHeader>

        <div className="mb-6">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium">
              {i18n.getCompletenessDrilldownText('labels.overallCompleteness') ||
                'Overall Completeness'}
            </span>
            <span
              className={
                percent >= 80
                  ? 'text-green-600'
                  : percent >= 50
                    ? 'text-yellow-600'
                    : 'text-red-600'
              }
            >
              {percent}%
            </span>
          </div>

          <div className="relative">
            <Progress
              value={percent}
              className={`h-3 ${percent >= 80 ? 'bg-green-500' : percent >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
            />

            {/* Threshold markers */}
            <div className="absolute left-[60%] top-0 h-3 border-r border-white/50"></div>
            <div className="absolute left-[80%] top-0 h-3 border-r border-white/50"></div>
            <div className="absolute left-[95%] top-0 h-3 border-r border-white/50"></div>

            {/* Threshold labels */}
            <div className="mt-1 flex justify-between text-xs text-slate-500">
              <span>
                {i18n.getCompletenessDrilldownText('completenessLevels.poor.text') || 'Poor'}
              </span>
              <span>
                {i18n.getCompletenessDrilldownText('completenessLevels.fair.text') || 'Fair'}
              </span>
              <span>
                {i18n.getCompletenessDrilldownText('completenessLevels.good.text') || 'Good'}
              </span>
              <span>
                {i18n.getCompletenessDrilldownText('completenessLevels.excellent.text') ||
                  'Excellent'}
              </span>
            </div>
          </div>
        </div>

        {/* Field Status Table */}
        {fieldStatus.length > 0 && (
          <div className="mb-5">
            <h3 className="mb-2 text-sm font-medium">
              {i18n.getCompletenessDrilldownText('labels.fieldCompleteness') ||
                'Field Completeness Status'}
            </h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {i18n.getCompletenessDrilldownText('labels.field') || 'Field'}
                  </TableHead>
                  <TableHead>
                    {i18n.getCompletenessDrilldownText('labels.status') || 'Status'}
                  </TableHead>
                  <TableHead className="text-right">
                    {i18n.getCompletenessDrilldownText('labels.weight') || 'Weight'}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fieldStatus.map(field => (
                  <TableRow key={getFieldKey(field)}>
                    <TableCell className="font-medium">{getFieldLabel(field)}</TableCell>
                    <TableCell>
                      {field.complete ? (
                        <div className="flex items-center text-green-600">
                          <CheckCircle className="mr-1 size-4" />
                          <span>
                            {i18n.getCompletenessDrilldownText('statuses.complete') || 'Complete'}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center text-red-500">
                          <AlertCircle className="mr-1 size-4" />
                          <span>
                            {i18n.getCompletenessDrilldownText('statuses.missing') || 'Missing'}
                          </span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex cursor-help items-center justify-end space-x-1">
                              <span>{field.weight}x</span>
                              <Info className="size-3 text-slate-400" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="left">
                            <p className="text-xs">
                              Weight factor: <strong>{field.weight}×</strong>
                              <br />
                              {field.weight >= 2
                                ? i18n.getCompletenessDrilldownText('weightTooltips.field.high') ||
                                  'Critical field for product quality'
                                : field.weight >= 1.5
                                  ? i18n.getCompletenessDrilldownText(
                                      'weightTooltips.field.medium'
                                    ) || 'Important field'
                                  : i18n.getCompletenessDrilldownText('weightTooltips.field.low') ||
                                    'Standard field'}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Attribute Fields Status Table */}
        {groups.map(group => (
          <div key={group.groupId} className="mb-5">
            <h3 className="mb-2 text-sm font-medium">{group.groupName}</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{labels.labels.attribute}</TableHead>
                  <TableHead>{labels.labels.status}</TableHead>
                  <TableHead className="text-right">{labels.labels.weight}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {group.attributes.map(attribute => (
                  <TableRow key={attribute.id}>
                    <TableCell className="font-medium">{attribute.label}</TableCell>
                    <TableCell>
                      {attribute.value != null && attribute.value !== '' ? (
                        <div className="flex items-center text-green-600">
                          <CheckCircle className="mr-1 size-4" />
                          <span>
                            {i18n.getCompletenessDrilldownText('statuses.complete') || 'Complete'}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center text-red-500">
                          <AlertCircle className="mr-1 size-4" />
                          <span>
                            {i18n.getCompletenessDrilldownText('statuses.missing') || 'Missing'}
                          </span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex cursor-help items-center justify-end space-x-1">
                              <span>1×</span>
                              <Info className="size-3 text-slate-400" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="left">
                            <p className="text-xs">
                              Weight factor: <strong>1×</strong>
                              <br />
                              {i18n.getCompletenessDrilldownText('weightTooltips.attribute.low') ||
                                'Standard attribute'}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}

        {/* Summary */}
        <div className="mb-5 space-y-2">
          <div className="flex justify-between text-sm">
            <span>
              {i18n.getCompletenessDrilldownText('labels.completeFields') || 'Complete Fields'}
            </span>
            <span>
              {filled} of {total}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span>
              {i18n.getCompletenessDrilldownText('labels.missingFields') || 'Missing Fields'}
            </span>
            <span className={total - filled > 0 ? 'font-medium text-red-500' : ''}>
              {total - filled}
            </span>
          </div>
          {groups.length > 0 && (
            <>
              <div className="flex justify-between text-sm">
                <span>
                  {i18n.getCompletenessDrilldownText('labels.completeAttributes') ||
                    'Complete Attributes'}
                </span>
                <span>
                  {filled} of {total}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>
                  {i18n.getCompletenessDrilldownText('labels.missingAttributes') ||
                    'Missing Attributes'}
                </span>
                <span className={total - filled > 0 ? 'font-medium text-red-500' : ''}>
                  {total - filled}
                </span>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end">
          <SheetClose asChild>
            <Button variant="outline">
              {i18n.getCompletenessDrilldownText('buttons.close') || 'Close'}
            </Button>
          </SheetClose>
        </div>
      </SheetContent>
    </Sheet>
  );
};
